import { createFileRoute, Link } from "@tanstack/react-router";
import { SetupCopyBlock } from "@/components/setup/SetupCopyBlock";

export const Route = createFileRoute("/help/mcp-tools")({
	component: McpTools,
});

const tools = [
	{
		category: "Generate",
		items: [
			{
				name: "generate_image",
				description:
					"Create an image from a prompt. Supports model overrides, size controls, workspace storage, and a durable output URL.",
				examplePrompt:
					"Generate a clean product hero image for this new skincare bottle in a soft studio setup.",
			},
			{
				name: "upscale_image",
				description:
					"Upscale an existing image by generation ID or URL, then store and return the final asset.",
				examplePrompt:
					"Upscale the last product image and give me the final URL.",
			},
			{
				name: "generate_video",
				description:
					"Create a short video from a text prompt. Useful for ads, teasers, demos, and campaign clips.",
				examplePrompt:
					"Generate a 5 second 16:9 video of the perfume bottle rotating on a black pedestal.",
			},
			{
				name: "generate_audio",
				description:
					"Create audio from a prompt for soundtracks, loops, brand stings, and background music.",
				examplePrompt:
					"Create a 30 second ambient track for a premium fashion teaser.",
			},
		],
	},
	{
		category: "Models",
		items: [
			{
				name: "list_models",
				description:
					"List available image, video, and audio models with defaults and supported options.",
				examplePrompt:
					"Which image and video models can I use right now, and what is the default?",
			},
		],
	},
	{
		category: "Assets",
		items: [
			{
				name: "list_recent_generations",
				description:
					"List recent stored generations in the active workspace so an assistant can continue from earlier assets.",
				examplePrompt: "Show me the assets generated in this workspace today.",
			},
			{
				name: "get_generation",
				description:
					"Fetch one generation by ID, including status, prompt, metadata, and output URL.",
				examplePrompt: "Get the details and output URL for generation gen_123.",
			},
		],
	},
	{
		category: "Billing",
		items: [
			{
				name: "get_credits",
				description:
					"Return the prepaid balance available to the active workspace before starting generation.",
				examplePrompt: "How much prepaid balance is left in this workspace?",
			},
			{
				name: "top_up_balance",
				description:
					"Create a Stripe Checkout URL to add prepaid balance. Generation runs only when balance is available.",
				examplePrompt:
					"Top up this workspace with 25 dollars before creating the assets.",
			},
		],
	},
	{
		category: "Workspace",
		items: [
			{
				name: "list_workspaces",
				description:
					"List workspaces available to the account with IDs, slugs, names, and role context.",
				examplePrompt:
					"List my AgentFramer workspaces and show which one is active.",
			},
			{
				name: "create_workspace",
				description:
					"Create a workspace and make it active for future MCP calls.",
				examplePrompt: "Create a workspace called Summer Campaign.",
			},
			{
				name: "switch_workspace",
				description:
					"Switch the active MCP workspace. Future generation, billing, and team calls use that workspace.",
				examplePrompt: "Switch to my Brand Assets workspace.",
			},
			{
				name: "delete_workspace",
				description:
					"Delete the active workspace when the user explicitly asks. If another workspace remains, the session moves there.",
				examplePrompt: "Delete this test workspace after I confirm.",
			},
		],
	},
	{
		category: "Team",
		items: [
			{
				name: "invite_team_member",
				description: "Invite someone to the active workspace by email.",
				examplePrompt: "Invite maria@example.com to this workspace.",
			},
			{
				name: "list_members",
				description: "List workspace members and pending invitations.",
				examplePrompt: "Who has access to this workspace?",
			},
		],
	},
	{
		category: "Feedback",
		items: [
			{
				name: "submit_feedback",
				description:
					"Send product feedback to the AgentFramer team without opening live chat.",
				examplePrompt:
					"Send this as product feedback: model selection needs folders.",
			},
		],
	},
];

export function McpTools() {
	const totalTools = tools.reduce((sum, cat) => sum + cat.items.length, 0);

	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<h1 className="text-2xl font-medium tracking-tight">
					MCP tools reference
				</h1>
				<p className="text-sm text-muted-foreground">
					All {totalTools} tools available through Model Context Protocol.
				</p>
			</div>

			<div className="space-y-8">
				{tools.map((category) => (
					<section key={category.category} className="space-y-3">
						<h2 className="text-base font-medium text-foreground">
							{category.category}
						</h2>
						<div className="space-y-4">
							{category.items.map((tool) => (
								<div key={tool.name} className="space-y-2">
									<p>
										<code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-medium text-foreground">
											{tool.name}
										</code>
									</p>
									<p className="text-sm leading-relaxed text-muted-foreground">
										{tool.description}
									</p>
									<SetupCopyBlock text={tool.examplePrompt} multiline />
								</div>
							))}
						</div>
					</section>
				))}
			</div>

			<div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
				<p>
					Each tool accepts structured parameters, but you do not need to know
					the exact schema. Describe the asset you want and your assistant maps
					that intent to the right AgentFramer call.
				</p>
				<p>
					For setup instructions, see{" "}
					<Link
						to="/help/mcp"
						className="text-primary transition-colors hover:text-primary/80"
					>
						MCP overview
					</Link>
					.
				</p>
			</div>

			<div className="flex items-center gap-4 text-sm">
				<Link
					to="/help/mcp"
					className="text-muted-foreground transition-colors hover:text-foreground"
				>
					&larr; MCP overview
				</Link>
			</div>
		</article>
	);
}
